import React, { useState, useEffect } from 'react';
import logo from '../images/logo.svg';
import '../scss/playerRatings.scss';
import axios from 'axios';

function StageTwo({ playerAbilities, onSubmit, onBack, inviteCode, isUpdate = false }) {
  const [localAbilities, setLocalAbilities] = useState({
    ...playerAbilities,
    // Convert stored 0-100 values to display 0-10 values
    displayPace: Math.round(playerAbilities.pace / 10),
    displayShooting: Math.round(playerAbilities.shooting / 10),
    displayPassing: Math.round(playerAbilities.passing / 10),
    displayDribbling: Math.round(playerAbilities.dribbling / 10),
    displayDefending: Math.round(playerAbilities.defending / 10),
    displayPhysical: Math.round(playerAbilities.physical / 10)
  });
  const [isAnimating, setIsAnimating] = useState(false);
  const [availableShirtNumbers, setAvailableShirtNumbers] = useState([]);
  const [selectedShirtNumber, setSelectedShirtNumber] = useState('');
  const [shirtNumberError, setShirtNumberError] = useState('');
  const [isCheckingShirtNumber, setIsCheckingShirtNumber] = useState(false);
  const [ratingsError, setRatingsError] = useState('');
  
  const positions = [
    { value: 'GK', label: 'Goalkeeper' },
    { value: 'CB', label: 'Center Back' },
    { value: 'LB', label: 'Left Back' },
    { value: 'RB', label: 'Right Back' },
    { value: 'CDM', label: 'Defending Midfielder' },
    { value: 'CM', label: 'Central Midfielder' },
    { value: 'CAM', label: 'Attacking Midfielder' },
    { value: 'LW', label: 'Left Winger' },
    { value: 'RW', label: 'Right Winger' },
    { value: 'ST', label: 'Striker' }
  ];
  
  const footOptions = [
    'Left',
    'Right',
    'Both'
  ];
  
  useEffect(() => {
    // Small delay before starting animation
    const timer = setTimeout(() => {
      setIsAnimating(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // Load available shirt numbers when component mounts
  useEffect(() => {
    const fetchAvailableShirtNumbers = async () => {
      try {
        // Extract team ID from invite code (handle both old and new formats)
        let teamId = 1; // Default value
        if (inviteCode) {
          const match = inviteCode.match(/^(\d+)-(\d{4})(?:-(\d+))?$/);
          if (match) {
            teamId = parseInt(match[1]);
          }
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/register/get_available_shirt_numbers.php?team_id=${teamId}`
        );

        if (response.data.success) {
          setAvailableShirtNumbers(response.data.available_numbers || []);
          if (response.data.suggested_number) {
            setSelectedShirtNumber(response.data.suggested_number.toString());
            setLocalAbilities(prev => ({ ...prev, shirtNumber: response.data.suggested_number.toString() }));
          }
        }
      } catch (error) {
        console.error('Error fetching available shirt numbers:', error);
      }
    };

    fetchAvailableShirtNumbers();
  }, [inviteCode]);
  
  // Initialize progress indicators when component mounts
  useEffect(() => {
    const abilities = ['pace', 'shooting', 'passing', 'dribbling', 'defending', 'physical'];
    abilities.forEach(ability => {
      const slider = document.getElementById(`${ability}-slider`);
      if (slider) {
        const displayValue = localAbilities[`display${ability.charAt(0).toUpperCase() + ability.slice(1)}`] || 0;
        const percent = (displayValue / 10) * 100;
        slider.style.setProperty('--value-percent', `${percent}%`);
      }
    });
  }, [localAbilities]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalAbilities({ ...localAbilities, [name]: value });

    // Handle shirt number change
    if (name === 'shirtNumber') {
      setSelectedShirtNumber(value);
      validateShirtNumber(value);
    }
  };

  const validateShirtNumber = async (number) => {
    if (!number) {
      setShirtNumberError('Please select a shirt number');
      return false;
    }

    // Check if the number is in the available numbers list
    if (!availableShirtNumbers.includes(parseInt(number))) {
      setShirtNumberError('This shirt number is already taken');
      return false;
    }

    setShirtNumberError('');
    return true;
  };
  
  const handleRatingChange = (ability, displayValue) => {
    // Convert display value (0-10) to stored value (0-100)
    const storedValue = parseInt(displayValue) * 10;
    
    setLocalAbilities(prevAbilities => {
      // Update the slider's progress indicator
      const slider = document.getElementById(`${ability}-slider`);
      if (slider) {
        const percent = (displayValue / 10) * 100;
        slider.style.setProperty('--value-percent', `${percent}%`);
      }
      
      return { 
        ...prevAbilities, 
        [ability]: storedValue,
        [`display${ability.charAt(0).toUpperCase() + ability.slice(1)}`]: parseInt(displayValue)
      };
    });
    
    // Clear ratings error when user changes a rating
    setRatingsError('');
  };
  
  const handleFootPreference = (foot) => {
    setLocalAbilities({ ...localAbilities, preferredFoot: foot });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate shirt number before submitting
    const isShirtNumberValid = await validateShirtNumber(selectedShirtNumber);
    if (!isShirtNumberValid) {
      return;
    }
    
    // Check if all ability ratings are the same
    const abilityValues = [
      localAbilities.pace, 
      localAbilities.shooting, 
      localAbilities.passing, 
      localAbilities.dribbling, 
      localAbilities.defending, 
      localAbilities.physical
    ];
    
    const allSameValue = abilityValues.every(val => val === abilityValues[0]);
    const allDefaultValue = abilityValues.every(val => val === 50);
    
    if (allSameValue || allDefaultValue) {
      // Display an error message
      setRatingsError('Please adjust your player ratings. They cannot all be the same value.');
      // Scroll to the ratings section
      document.querySelector('.player-ratings-container').scrollIntoView({ behavior: 'smooth' });
      return;
    }
    
    // Remove display values before submitting
    const { 
      displayPace, displayShooting, displayPassing, 
      displayDribbling, displayDefending, displayPhysical, 
      ...submitData 
    } = localAbilities;

    // Add shirt number to submit data
    submitData.shirtNumber = selectedShirtNumber;
    
    onSubmit(submitData);
  };
  
  // Calculate positions for hexagon points
  const calculatePoint = (value, index, total = 6) => {
    const angle = (Math.PI * 2 * index) / total - Math.PI / 2;
    const radius = (value / 100) * 50; // 50 is the max radius
    return {
      x: 50 + radius * Math.cos(angle),
      y: 50 + radius * Math.sin(angle)
    };
  };

  // Generate points for the polygon
  const points = [
    calculatePoint(localAbilities.pace, 0),
    calculatePoint(localAbilities.shooting, 1),
    calculatePoint(localAbilities.passing, 2),
    calculatePoint(localAbilities.physical, 3),
    calculatePoint(localAbilities.defending, 4),
    calculatePoint(localAbilities.dribbling, 5),
  ];

  const polygonPoints = points.map(p => `${p.x},${p.y}`).join(' ');
  
  return (
    <div className="register-stage">
      <div className="stage-indicator">
        <div className="stage">1</div>
        <div className="stage-line"></div>
        <div className="stage active">2</div>
      </div>
      
      <h3 className="stage-title">{isUpdate ? 'Update Player Abilities' : 'Player Abilities'}</h3>
      
      <form onSubmit={handleSubmit}>
        
        <div className="form-group">
          <div className="foot-preference">
            <label>Preferred Foot</label>
            <div className="foot-options">
              {footOptions.map(option => (
                <div 
                  key={option}
                  className={`foot-option ${localAbilities.preferredFoot === option ? 'selected' : ''}`}
                  onClick={() => handleFootPreference(option)}
                >
                  {option}
                </div>
              ))}
            </div>
          </div>
          
          <label>Position</label>
          <select 
            name="position" 
            value={localAbilities.position} 
            onChange={handleChange}
            className="position-select"
          >
            {positions.map(position => (
              <option key={position.label} value={position.value}>{position.label}</option>
            ))}
          </select>
          
          <label>Shirt Number</label>
          <select
            name="shirtNumber"
            value={selectedShirtNumber}
            onChange={handleChange}
            className="shirt-number-select"
            required
          >
            <option value="">Select Shirt Number</option>
            {availableShirtNumbers.map(number => (
              <option key={number} value={number.toString()}>
                {number}
              </option>
            ))}
          </select>
          {shirtNumberError && <div className="error-message">{shirtNumberError}</div>}
          
          <div className="player-ratings-container">
            <h4>Rate Your Abilities</h4>
            <p className="rating-instructions">Drag the sliders to rate your football abilities</p>
            
            <div className="player-ratings">
              <div className="ratings-hexagon">
                <svg viewBox="0 0 100 100" className="ratings-chart">
                  {/* Background grid */}
                  <g className="grid">
                    {[20, 40, 60, 80, 100].map((percent, i) => (
                      <polygon
                        key={i}
                        points={[0, 1, 2, 3, 4, 5].map(j => {
                          const p = calculatePoint(percent, j);
                          return `${p.x},${p.y}`;
                        }).join(' ')}
                        className="grid-line"
                      />
                    ))}
                  </g>
                  
                  {/* Stats polygon */}
                  <polygon 
                    points={polygonPoints} 
                    className={`stats-polygon ${isAnimating ? 'animate' : ''}`} 
                  />
                  
                  {/* Stat points */}
                  {points.map((point, i) => (
                    <circle
                      key={i}
                      cx={point.x}
                      cy={point.y}
                      r="2"
                      className={`stat-point ${isAnimating ? 'animate' : ''}`}
                    />
                  ))}
                </svg>
                
                {/* Labels */}
                <div className="stat-label pace">PAC</div>
                <div className="stat-label shooting">SHO</div>
                <div className="stat-label passing">PAS</div>
                <div className="stat-label physical">PHY</div>
                <div className="stat-label defending">DEF</div>
                <div className="stat-label dribbling">DRI</div>
              </div>
            </div>
            
            <div className="rating-sliders">
              <div className="rating-slider">
                <label>
                  <span className="slider-label">Pace</span>
                  <span className="slider-value">{localAbilities.displayPace}<span className="scale-indicator">/10</span></span>
                </label>
                <input 
                  id="pace-slider"
                  type="range" 
                  min="0" 
                  max="10" 
                  value={localAbilities.displayPace} 
                  onChange={(e) => handleRatingChange('pace', e.target.value)} 
                />
              </div>
              
              <div className="rating-slider">
                <label>
                  <span className="slider-label">Shooting</span>
                  <span className="slider-value">{localAbilities.displayShooting}<span className="scale-indicator">/10</span></span>
                </label>
                <input 
                  id="shooting-slider"
                  type="range" 
                  min="0" 
                  max="10" 
                  value={localAbilities.displayShooting} 
                  onChange={(e) => handleRatingChange('shooting', e.target.value)} 
                />
              </div>
              
              <div className="rating-slider">
                <label>
                  <span className="slider-label">Passing</span>
                  <span className="slider-value">{localAbilities.displayPassing}<span className="scale-indicator">/10</span></span>
                </label>
                <input 
                  id="passing-slider"
                  type="range" 
                  min="0" 
                  max="10" 
                  value={localAbilities.displayPassing} 
                  onChange={(e) => handleRatingChange('passing', e.target.value)} 
                />
              </div>
              
              <div className="rating-slider">
                <label>
                  <span className="slider-label">Dribbling</span>
                  <span className="slider-value">{localAbilities.displayDribbling}<span className="scale-indicator">/10</span></span>
                </label>
                <input 
                  id="dribbling-slider"
                  type="range" 
                  min="0" 
                  max="10" 
                  value={localAbilities.displayDribbling} 
                  onChange={(e) => handleRatingChange('dribbling', e.target.value)} 
                />
              </div>
              
              <div className="rating-slider">
                <label>
                  <span className="slider-label">Defending</span>
                  <span className="slider-value">{localAbilities.displayDefending}<span className="scale-indicator">/10</span></span>
                </label>
                <input 
                  id="defending-slider"
                  type="range" 
                  min="0" 
                  max="10" 
                  value={localAbilities.displayDefending} 
                  onChange={(e) => handleRatingChange('defending', e.target.value)} 
                />
              </div>
              
              <div className="rating-slider">
                <label>
                  <span className="slider-label">Physical</span>
                  <span className="slider-value">{localAbilities.displayPhysical}<span className="scale-indicator">/10</span></span>
                </label>
                <input 
                  id="physical-slider"
                  type="range" 
                  min="0" 
                  max="10" 
                  value={localAbilities.displayPhysical} 
                  onChange={(e) => handleRatingChange('physical', e.target.value)} 
                />
              </div>
              
              {ratingsError && <div className="error-message ratings-error">{ratingsError}</div>}
            </div>
          </div>
        </div>
        
        <div className="button-group">
          <button type="submit" className="submit-button">
            {isUpdate ? 'Update Profile' : 'Complete Registration'}
          </button>
          <div className="form-links">
            <button type="button" className="back-button" onClick={onBack}>
              <span className="chevron-left">←</span> Back
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default StageTwo; 